import { useCallback, useEffect, useState, useRef } from 'react';
import { isApiError } from '@aiptyg/types';
import { useToastStore } from '../../../store/toast.store';
import * as settingsService from '../services/settings.service';
import type { NewcomerRewardPolicyDTO } from '../types/settings.types';

interface NewcomerRewardPolicyForm {
  enabled: boolean;
  amountYuan: string;
}

const EMPTY_FORM: NewcomerRewardPolicyForm = {
  enabled: false,
  amountYuan: '1.00',
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid newcomer reward policy': '新人奖励配置无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function dtoToForm(dto: NewcomerRewardPolicyDTO, exponent: number): NewcomerRewardPolicyForm {
  return {
    enabled: dto.enabled,
    amountYuan: (dto.amount_fen / 10 ** exponent).toFixed(exponent),
  };
}

function yuanToFen(value: string, exponent: number): number {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.round(amount * 10 ** exponent);
}

export function useNewcomerRewardPolicy(selectedMarketId = 'CN', exponent = 2) {
  const requestId = useRef(0);
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<NewcomerRewardPolicyDTO | null>(null);
  const [form, setForm] = useState<NewcomerRewardPolicyForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getNewcomerRewardPolicy(selectedMarketId);
      if (id !== requestId.current) return;
      if (result.market_id !== selectedMarketId) throw new Error('奖励配置地区不匹配');
      setData(result);
      setForm(dtoToForm(result, exponent));
    } catch (e) {
      if (id === requestId.current) setError(resolveError(e));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [selectedMarketId, exponent]);

  useEffect(() => {
    load();
    return () => { requestId.current++; };
  }, [load]);

  const setEnabled = useCallback((enabled: boolean) => {
    setForm((prev) => ({ ...prev, enabled }));
  }, []);

  const setAmountYuan = useCallback((amountYuan: string) => {
    setForm((prev) => ({ ...prev, amountYuan }));
  }, []);

  const save = useCallback(async () => {
    const amountFen = yuanToFen(form.amountYuan, exponent);
    if (amountFen <= 0) {
      addToast('error', '新人奖励金额必须大于 0');
      return;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateNewcomerRewardPolicy({
        market_id: selectedMarketId,
        enabled: form.enabled,
        amountFen,
      });
      setData(result);
      setForm(dtoToForm(result, exponent));
      addToast('success', '新人注册奖励配置已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [addToast, form, selectedMarketId, exponent]);

  const amountFen = yuanToFen(form.amountYuan, exponent);

  return {
    data,
    form,
    amountFen,
    loading: loading || (!error && data?.market_id !== selectedMarketId),
    saving,
    error,
    setEnabled,
    setAmountYuan,
    save,
    reload: load,
  };
}
